<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { emitter } from 'shared/helpers/mitt';
import router from '../../../../index';

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  props: {
    inbox: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      syncHistory: false,
      syncDays: 1,
      isCreated: false,
      isConnected: false,
      inboxId: null,
      qrCode: null,
      isLoadingQR: false,
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    isReconnection() {
      return !!this.inbox;
    },
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
  },
  mounted() {
    console.log('[BaileysDebug] Component Mounted, listening for events');
    emitter.on('baileys_connection_success', this.onConnectionSuccess);
    emitter.on('baileys_qr_update', this.onQRUpdate);

    if (this.inbox) {
      console.log('[BaileysDebug] Started in Reconnection mode for inbox:', this.inbox.id);
      this.inboxId = this.inbox.id;
      this.isCreated = true;
      // If we are in reconnection mode, we don't fetch QR automatically 
      // unless we know we need it. But usually we want to start the process.
      this.reconnect();
    } else {
      // PERSISTENCE: Recover state if refresh happened (only for new inbox setup)
      const savedInboxId = localStorage.getItem('baileys_setup_inbox_id');
      if (savedInboxId) {
        console.log('[BaileysDebug] Recovered inboxId from localStorage:', savedInboxId);
        this.inboxId = Number(savedInboxId);
        this.isCreated = true;
        this.fetchQRCode();
      }
    }
  },
  beforeUnmount() {
    console.log('[BaileysDebug] Component Unmounted, cleaning up listeners');
    emitter.off('baileys_connection_success', this.onConnectionSuccess);
    emitter.off('baileys_qr_update', this.onQRUpdate);
  },
  methods: {
    onConnectionSuccess(data) {
      if (!this.inboxId) return;
      console.log('[BaileysDebug] Received connection success:', data, 'Expected Inbox ID:', this.inboxId);
      if (data.inbox_id === this.inboxId) {
        console.log('[BaileysDebug] IDs match! Updating state to connected.');
        this.isConnected = true;
        this.isCreated = true;
        localStorage.removeItem('baileys_setup_inbox_id'); // SUCCESS, clear it
        if (this.isReconnection) {
          useAlert('WhatsApp Reconectado com Sucesso!');
        }
      } else {
        console.warn('[BaileysDebug] ID Mismatch in connection success!');
      }
    },
    onQRUpdate(data) {
      if (!this.inboxId) return;
      console.log('[BaileysDebug] Received QR update:', data, 'Expected Inbox ID:', this.inboxId);
      if (data.inbox_id === this.inboxId) {
         console.log('[BaileysDebug] IDs match! Updating QR.');
         this.qrCode = data.qr;
      }
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      try {
        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName?.trim(),
            channel: {
              type: 'whatsapp',
              phone_number: this.phoneNumber,
              provider: 'embedded_baileys',
              provider_config: {
                sync_history_days: this.syncHistory ? this.syncDays : 0
              },
            },
          }
        );
        this.isCreated = true;
        this.inboxId = whatsappChannel.id;
        localStorage.setItem('baileys_setup_inbox_id', this.inboxId); // SAVE for persistence
        this.fetchQRCode();
      } catch (error) {
        useAlert(error.message || 'Erro ao criar canal.');
      }
    },
    async fetchQRCode() {
      this.isLoadingQR = true;
      try {
        const response = await window.axios.get(
          `/api/v1/accounts/${this.$store.getters['getCurrentAccountId']}/whatsapp/baileys/${this.inboxId}/show_qr`
        );
        this.qrCode = response.data.qr;
      } catch (error) {
        if (error.response?.status === 404) {
          console.warn('[BaileysDebug] Stale state detected (404). Resetting setup state.');
          this.resetSetupState();
        } else {
          useAlert('Erro ao buscar QR Code.');
        }
      } finally {
        this.isLoadingQR = false;
      }
    },
    async reconnect() {
      this.isLoadingQR = true;
      this.qrCode = null;
      this.isConnected = false;
      try {
        // Force logout to ensure bridge generates new QR
        await window.axios.post(
          `/api/v1/accounts/${this.$store.getters['getCurrentAccountId']}/whatsapp/baileys/${this.inboxId}/logout`
        );
        await this.fetchQRCode();
      } catch (error) {
        useAlert('Erro ao iniciar reconexão.');
      } finally {
        this.isLoadingQR = false;
      }
    },
    resetSetupState() {
      localStorage.removeItem('baileys_setup_inbox_id');
      this.inboxId = null;
      this.isCreated = false;
      this.isConnected = false;
      this.qrCode = null;
    },
    goToAddAgents() {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: {
          page: 'new',
          inbox_id: this.inboxId,
        },
      });
    },
  },
};
</script>

<template>
  <div class="baileys-setup">
    <form v-if="!isCreated" @submit.prevent="createChannel()">
      <div class="mb-4">
        <label :class="{ error: v$.inboxName.$error }">
          Nome da Caixa de Entrada
          <input v-model="inboxName" type="text" placeholder="Ex: WhatsApp Suporte" />
        </label>
      </div>
      <div class="mb-4">
        <label :class="{ error: v$.phoneNumber.$error }">
          Número do Telefone (E.164)
          <input v-model="phoneNumber" type="text" placeholder="+5511999999999" />
        </label>
      </div>

      <div class="mb-4 p-4 border rounded bg-gray-50">
        <label class="flex items-center cursor-pointer mb-2">
          <input type="checkbox" v-model="syncHistory" style="width: 20px; height: 20px; cursor: pointer; margin-right: 10px;" />
          <div class="text-gray-700 font-medium" style="font-size: 16px;">
            Ativar Sincronização de Histórico Antigo (Opcional)
          </div>
        </label>
        
        <div v-if="syncHistory" class="mt-3 pl-8">
          <label class="text-sm text-gray-600">
            Quantidade de Dias (Mínimo 1, Máximo 7):
            <input 
              v-model.number="syncDays" 
              type="number" 
              min="1" 
              max="7"
              class="mt-1 block w-full rounded border-gray-300 p-2" 
              style="width: 150px; border: 1px solid #ccc;"
            />
            <p class="text-xs text-gray-500 mt-1">O histórico será puxado lentamente em segundo plano.</p>
          </label>
        </div>
      </div>

      <NextButton :is-loading="uiFlags.isCreating" type="submit" label="Criar e Gerar QR Code" />
    </form>

    <div v-else class="text-center p-4">
      <div v-if="isConnected">
        <div class="mb-4">
          <i class="ion-checkmark-circled text-4xl text-green-500"></i>
        </div>
        <h3 class="text-lg font-medium mb-4">Conectado com Sucesso!</h3>
        <p class="mb-6 text-gray-600">Sua conta do WhatsApp está sincronizada e pronta para uso.</p>
        <NextButton 
          label="Concluir Configuração" 
          @click="goToAddAgents" 
        />
      </div>

      <div v-else>
        <h3 class="text-lg font-medium mb-4">Escaneie o QR Code</h3>
        <div v-if="qrCode" class="qr-container bg-white p-4 inline-block rounded shadow">
          <img :src="qrCode" alt="WhatsApp QR Code" class="mx-auto" />
        </div>
        <div v-else-if="isLoadingQR" class="p-8">
          <p>Gerando QR Code...</p>
        </div>
        <div v-else>
          <p class="text-red-500">Falha ao carregar QR Code.</p>
          <button @click="fetchQRCode" class="mt-2 text-blue-500 underline">Tentar novamente</button>
        </div>
        <p class="mt-4 text-sm text-gray-600">
          Abra o WhatsApp no seu celular > Configurações > Aparelhos Conectados.
        </p>
        <button 
          class="mt-8 text-xs text-gray-400 hover:text-red-500 transition-colors"
          @click="resetSetupState"
        >
          Esquecer esta tentativa e voltar ao início
        </button>
      </div>
    </div>
  </div>
</template>

